// signals-web.ts — the WEB runtime's derived-signal computation.
//
// 🔴 WHY THIS EXISTS: `compute_signals` was a zero stub in web-commands.ts (`// TODO: port derived
// logic to JS`). On web every count came back 0 and every list empty — so the season recap and
// "Ask your stash", both BILLED calls, were handed an empty catalogue. We were charging for a model
// to describe possessions it could not see. Native is unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/signals.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// `lead_insights` and `headline_facts` are ported in full and deliberately: the recap OPENS with
// lead_insights[0], and both exist so the narration never computes (or invents) a figure. A port
// that shipped the aggregates but dropped the sentences would leave the model with numbers and no
// grounding — exactly the situation they were written to prevent.

import type { Item, StashSignals } from "./store";

const CYCLE_DAYS = 365;

const parse = (s: string | null | undefined): number | null => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const t = Date.parse(`${s}T00:00:00Z`);
  return Number.isNaN(t) ? null : t;
};

/** Mirror of signals.rs::within_cycle — used within the last 365 days, not in the future. */
function withinCycle(lastUsedOn: string | null, today: number): boolean {
  const d = parse(lastUsedOn);
  if (d === null) return false;
  return (today - d) / 86400000 <= CYCLE_DAYS && today >= d;
}

/** Mirror of signals.rs::loc_label. */
const locLabel = (location: string) => (location.trim() ? location.trim() : "(no location)");

/** Mirror of the `title` closure — title-case a normalized token. */
const title = (t: string) =>
  t.split(/[\s-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

const plur = (n: number, s: string) => `${n} ${s}${n === 1 ? "" : "s"}`;

/** English list join: ["A","B","C"] -> "A, B and C". */
function list(xs: string[]): string {
  if (xs.length === 0) return "";
  if (xs.length === 1) return xs[0];
  if (xs.length === 2) return `${xs[0]} and ${xs[1]}`;
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

const FOCUS_ITEM_CAP = 60;
const GROUP_NAME_CAP = 20;

interface SeasonRollup { season: string; items: number; units: number; locations: number; used_last_cycle: number; never_used: number }
interface LocationRollup { location: string; items: number; units: number; seasons: string[] }
interface DuplicateCluster { name: string; season: string; rows: number; total_units: number; locations: string[] }
interface LocationGroup { location: string; items: number; units: number; item_names: string[] }
interface SeasonFocus {
  season: string; total_items: number; total_units: number;
  locations: LocationGroup[]; never_used_items: string[];
  items: { name: string; category: string; occasion: string; location: string; quantity: number; last_used_on: string | null; used_last_cycle: boolean }[];
}

/** Mirror of signals.rs::compute. `todayStr` is the local calendar date. */
export function computeSignalsWeb(all: Item[], todayStr: string, focusSeason?: string | null): StashSignals {
  const today = parse(todayStr) ?? Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);

  // ── Overall ────────────────────────────────────────────────────────────────
  const distinctOf = (xs: string[]) => new Set(xs.filter(Boolean)).size;
  const overall = {
    total_items: all.length,
    total_units: all.reduce((n, i) => n + i.quantity, 0),
    distinct_seasons: distinctOf(all.map((i) => i.season)),
    distinct_locations: new Set(all.map((i) => locLabel(i.location))).size,
    distinct_categories: distinctOf(all.map((i) => i.category)),
    used_last_cycle: all.filter((i) => withinCycle(i.last_used_on, today)).length,
    never_used: all.filter((i) => !i.last_used_on).length,
    photographed: all.filter((i) => (i.photo_ref ?? "").trim()).length,
  };

  // ── by_season ──────────────────────────────────────────────────────────────
  const seasonAcc = new Map<string, { items: number; units: number; locs: Set<string>; used: number; never: number }>();
  for (const i of all) {
    const key = i.season || "(unsorted)";
    const e = seasonAcc.get(key) ?? { items: 0, units: 0, locs: new Set<string>(), used: 0, never: 0 };
    e.items += 1; e.units += i.quantity; e.locs.add(locLabel(i.location));
    if (withinCycle(i.last_used_on, today)) e.used += 1;
    if (!i.last_used_on) e.never += 1;
    seasonAcc.set(key, e);
  }
  const by_season: SeasonRollup[] = [...seasonAcc.entries()]
    .map(([season, e]) => ({ season, items: e.items, units: e.units, locations: e.locs.size, used_last_cycle: e.used, never_used: e.never }))
    .sort((a, b) => b.items - a.items || a.season.localeCompare(b.season));

  // ── by_location ────────────────────────────────────────────────────────────
  const locAcc = new Map<string, { items: number; units: number; seasons: Set<string> }>();
  for (const i of all) {
    const key = locLabel(i.location);
    const e = locAcc.get(key) ?? { items: 0, units: 0, seasons: new Set<string>() };
    e.items += 1; e.units += i.quantity;
    if (i.season) e.seasons.add(i.season);
    locAcc.set(key, e);
  }
  const by_location: LocationRollup[] = [...locAcc.entries()]
    .map(([location, e]) => ({ location, items: e.items, units: e.units, seasons: [...e.seasons].sort() }))
    .sort((a, b) => b.items - a.items || a.location.localeCompare(b.location));

  // ── by_category ────────────────────────────────────────────────────────────
  const catAcc = new Map<string, { items: number; units: number }>();
  for (const i of all) {
    const key = i.category || "(uncategorized)";
    const e = catAcc.get(key) ?? { items: 0, units: 0 };
    e.items += 1; e.units += i.quantity;
    catAcc.set(key, e);
  }
  const by_category = [...catAcc.entries()]
    .map(([category, e]) => ({ category, items: e.items, units: e.units }))
    .sort((a, b) => b.items - a.items || a.category.localeCompare(b.category));

  // ── duplicates: same normalized name+season across rows, OR one row with quantity > 1 ──
  const dupAcc = new Map<string, { rows: number; units: number; locs: Set<string>; display: string; season: string }>();
  for (const i of all) {
    const nameKey = i.name.trim().toLowerCase();
    if (!nameKey) continue;
    const key = `${nameKey} ${i.season}`;
    const e = dupAcc.get(key) ?? { rows: 0, units: 0, locs: new Set<string>(), display: i.name.trim(), season: i.season };
    e.rows += 1; e.units += i.quantity; e.locs.add(locLabel(i.location));
    dupAcc.set(key, e);
  }
  const duplicates: DuplicateCluster[] = [...dupAcc.values()]
    .filter((e) => e.rows > 1 || e.units > 1)
    .map((e) => ({ name: e.display, season: e.season, rows: e.rows, total_units: e.units, locations: [...e.locs].sort() }))
    .sort((a, b) => b.total_units - a.total_units || a.name.localeCompare(b.name))
    .slice(0, 12);

  // ── focus (one season) ─────────────────────────────────────────────────────
  let focus: SeasonFocus | null = null;
  const fs = (focusSeason ?? "").trim().toLowerCase();
  if (fs) {
    // Season token OR an occasion mentioning it — so "what do I have for Christmas?" also catches
    // items filed under season=winter but tagged occasion=Christmas.
    const matched = all
      .filter((i) => i.season === fs || (i.occasion ?? "").toLowerCase().includes(fs))
      .sort((a, b) => a.location.toLowerCase().localeCompare(b.location.toLowerCase())
        || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    if (matched.length) {
      const groupAcc = new Map<string, { items: number; units: number; names: string[] }>();
      for (const i of matched) {
        const key = locLabel(i.location);
        const e = groupAcc.get(key) ?? { items: 0, units: 0, names: [] };
        e.items += 1; e.units += i.quantity;
        if (e.names.length < GROUP_NAME_CAP) e.names.push(i.name.trim());
        groupAcc.set(key, e);
      }
      focus = {
        season: fs,
        total_items: matched.length,
        total_units: matched.reduce((n, i) => n + i.quantity, 0),
        locations: [...groupAcc.entries()]
          .map(([location, e]) => ({ location, items: e.items, units: e.units, item_names: e.names }))
          .sort((a, b) => b.items - a.items || a.location.localeCompare(b.location)),
        never_used_items: matched.filter((i) => !i.last_used_on).map((i) => i.name.trim()).slice(0, GROUP_NAME_CAP),
        items: matched.slice(0, FOCUS_ITEM_CAP).map((i) => ({
          name: i.name.trim(), category: i.category, occasion: i.occasion,
          location: locLabel(i.location), quantity: i.quantity,
          last_used_on: i.last_used_on, used_last_cycle: withinCycle(i.last_used_on, today),
        })),
      };
    }
  }

  return {
    schema:
      "oriora.stash.v2-signals: derived aggregates of OWNED possessions only (counts, locations, last-used dates, duplicate clusters, cross-location spread). headline_facts = pre-ranked, ready-to-say observational sentences already containing the figures — lead the narration with these, never invent a number not present in this object. focus (when set) holds the named items + their storage + last-used for the requested season. Each figure is a plain observation about the user's OWN catalog: describe what they own and where it is stored, with the use-log stated as plain fact. NEVER advise what to buy/wear/keep/discard/declutter, NEVER give styling/fashion/shopping advice, NEVER prescribe (say 'never logged as used', not 'use it more').",
    generated_for: todayStr,
    overall, by_season, by_location, by_category, duplicates, focus,
    headline_facts: buildHeadlineFacts(overall, by_season, by_location, duplicates, focus),
    lead_insights: buildCrossSignals(duplicates, by_season, focus),
  } as unknown as StashSignals;
}

/**
 * Mirror of signals.rs::build_cross_signals — up to 3 non-obvious findings, most striking first.
 * The recap OPENS with lead_insights[0], so this is the paid draw: patterns that need two columns
 * correlated and which the on-screen count bars genuinely cannot show.
 */
function buildCrossSignals(duplicates: DuplicateCluster[], by_season: SeasonRollup[], focus: SeasonFocus | null) {
  const out: { kind: string; headline: string }[] = [];

  // 1) redundancy_split — copies of one owned thing living in DIFFERENT real bins.
  const split = duplicates
    .filter((d) => d.total_units > 1 && d.locations.filter((l) => l !== "(no location)").length >= 2)
    .reduce<DuplicateCluster | null>((best, d) => {
      if (best === null) return d;
      if (d.total_units !== best.total_units) return d.total_units > best.total_units ? d : best;
      if (d.locations.length !== best.locations.length) return d.locations.length > best.locations.length ? d : best;
      return d.name < best.name ? d : best;   // Rust breaks the final tie on the LOWER name
    }, null);
  if (split) {
    const realLocs = split.locations.filter((l) => l !== "(no location)");
    const seasonNote = !split.season || split.season === "(unsorted)" ? "" : ` ${title(split.season)}`;
    out.push({
      kind: "redundancy_split",
      headline: `You own ${split.total_units} of your${seasonNote} "${split.name}" stored in ${realLocs.length} different spots — ${list(realLocs)}.`,
    });
  }

  // 2) dormant_season — a season where a strict MAJORITY has never been logged as used.
  const dormant = by_season
    .filter((s) => s.season !== "(unsorted)" && s.items >= 4 && s.never_used * 2 > s.items
      && (focus === null || focus.season !== s.season))
    .reduce<SeasonRollup | null>((best, s) => {
      if (best === null) return s;
      if (s.never_used !== best.never_used) return s.never_used > best.never_used ? s : best;
      if (s.items !== best.items) return s.items > best.items ? s : best;
      return s.season < best.season ? s : best;
    }, null);
  if (dormant) {
    const pct = Math.round((dormant.never_used / dormant.items) * 100);
    out.push({
      kind: "dormant_season",
      headline: `Of your ${dormant.items} ${title(dormant.season)} items, ${dormant.never_used} (${pct}%) have never been logged as used.`,
    });
  }

  return out.slice(0, 3);
}

/** Mirror of signals.rs::build_headline_facts. Order matters — the narration leads with fact #1. */
function buildHeadlineFacts(
  overall: { total_items: number; distinct_seasons: number; distinct_locations: number; never_used: number },
  by_season: SeasonRollup[],
  by_location: LocationRollup[],
  duplicates: DuplicateCluster[],
  focus: SeasonFocus | null,
) {
  const facts: { kind: string; text: string; tone: string }[] = [];

  // 1) The focused season — the "what do I have for X?" call leads here.
  if (focus && focus.total_items > 0) {
    const locN = focus.locations.length;
    const unitsNote = focus.total_units !== focus.total_items ? ` (${focus.total_units} total units)` : "";
    facts.push({
      kind: "season",
      text: `For ${title(focus.season)} you've cataloged ${plur(focus.total_items, "item")}${unitsNote} across ${plur(locN, "storage spot")}.`,
      tone: "neutral",
    });
    const top = focus.locations[0];
    if (top && locN > 1 && top.items > 1) {
      facts.push({ kind: "location", text: `Most of it — ${plur(top.items, "item")} — is in ${top.location}.`, tone: "neutral" });
    }
    if (focus.never_used_items.length) {
      facts.push({
        kind: "unused",
        text: `${plur(focus.never_used_items.length, "item")} for this season ${focus.never_used_items.length === 1 ? "has" : "have"} never been logged as used.`,
        tone: "watch",
      });
    }
  }

  // 2) The fullest storage spot across the whole catalogue.
  const topLoc = by_location.find((l) => l.location !== "(no location)");
  if (topLoc && topLoc.items >= 2 && overall.total_items > topLoc.items) {
    const seasonNote = topLoc.seasons.length > 1 ? `, spanning ${plur(topLoc.seasons.length, "season")}` : "";
    facts.push({
      kind: "spread",
      text: `Your fullest storage spot is ${topLoc.location} with ${plur(topLoc.items, "item")}${seasonNote}.`,
      tone: "neutral",
    });
  }

  // 3) Items with no storage spot recorded — a plain gap fact.
  const noloc = by_location.find((l) => l.location === "(no location)");
  if (noloc && noloc.items > 0 && overall.distinct_locations > 1) {
    facts.push({
      kind: "location",
      text: `${plur(noloc.items, "item")} ${noloc.items === 1 ? "has" : "have"} no storage spot recorded yet.`,
      tone: "watch",
    });
  }

  // 4) Never-used across the catalogue (only when a focus hasn't already covered it).
  if (focus === null && overall.never_used > 0 && overall.total_items > 0) {
    const pct = Math.round((overall.never_used / overall.total_items) * 100);
    facts.push({
      kind: "unused",
      text: `${plur(overall.never_used, "item")} of your ${plur(overall.total_items, "item")} (${pct}%) have never been logged as used.`,
      tone: "watch",
    });
  }

  // 5) The biggest duplicate cluster — a count that spans rows, so the eye misses it.
  const dup = duplicates[0];
  if (dup && dup.total_units > 1) {
    const whereNote = dup.locations.length > 1
      ? ` across ${plur(dup.locations.length, "spot")}`
      : dup.locations[0] ? ` in ${dup.locations[0]}` : "";
    facts.push({ kind: "duplicate", text: `You own ${dup.total_units} of "${dup.name}"${whereNote}.`, tone: "neutral" });
  }

  // 6) Breadth of the catalogue — a closing scale fact.
  if (focus === null && overall.total_items > 0) {
    const busiest = by_season.find((s) => s.season !== "(unsorted)");
    if (busiest && overall.distinct_seasons > 1) {
      facts.push({
        kind: "count",
        text: `Across ${plur(overall.distinct_seasons, "season")} you own ${plur(overall.total_items, "item")} — the most (${busiest.items}) tagged ${title(busiest.season)}.`,
        tone: "neutral",
      });
    }
  }

  return facts.slice(0, 6);
}
